/**
 * useNoteViewMode Hook
 *
 * Manages view mode state for the note editor:
 * - edit: Default editing mode
 * - completed: Note has been signed/completed
 * - transcript: Viewing transcript instead of note (orthogonal to edit/completed)
 *
 * Handles view mode transitions with analytics tracking.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useEventTracking } from '@services/statsig';

export type NoteViewMode = 'edit' | 'completed';

interface UseNoteViewModeOptions {
  noteId: string;
  encounterId?: string;
  completionStatus?: string;
}

interface UseNoteViewModeReturn {
  /** Current view mode (edit, completed) */
  viewMode: NoteViewMode;
  /** Whether in transcript mode */
  isTranscriptMode: boolean;
  /** Enter transcript viewing mode */
  enterTranscriptMode: () => void;
  /** Exit transcript mode back to note view */
  exitTranscriptMode: () => void;
}

export function useNoteViewMode({
  noteId,
  encounterId,
  completionStatus,
}: UseNoteViewModeOptions): UseNoteViewModeReturn {
  const { note: noteMetrics } = useEventTracking();

  // Local state for transcript mode
  const [isTranscriptMode, setIsTranscriptMode] = useState(false);

  // Track view mode changes for analytics (timestamp when entering each mode)
  const viewModeTimestampRef = useRef<{ timestamp: number; mode: string }>({
    timestamp: Date.now(),
    mode: 'note',
  });

  /**
   * Derive view mode from completion status
   */
  const viewMode = useMemo<NoteViewMode>(() => {
    if (completionStatus === 'completed' || completionStatus === 'ready' || completionStatus === 'signed') {
      return 'completed';
    }
    return 'edit'; // 'reviewing' and 'edits_requested' are both editable
  }, [completionStatus]);

  /**
   * Enter transcript viewing mode
   * Tracks analytics for view transcript action
   */
  const enterTranscriptMode = useCallback(() => {
    if (!noteId) return;

    const previousMode = isTranscriptMode ? 'transcript' : 'note';
    const timeInPreviousView = Date.now() - viewModeTimestampRef.current.timestamp;

    // Track switching to transcript view
    noteMetrics.viewTranscriptClicked(noteId, {
      previous_view: previousMode,
      time_in_previous_view_ms: timeInPreviousView,
      encounter_id: encounterId,
    });

    setIsTranscriptMode(true);

    // Update timestamp ref
    viewModeTimestampRef.current = {
      timestamp: Date.now(),
      mode: 'transcript',
    };
  }, [noteId, encounterId, isTranscriptMode, noteMetrics]);

  /**
   * Exit transcript mode back to note view
   * Tracks analytics for view note action
   */
  const exitTranscriptMode = useCallback(() => {
    if (!noteId) return;

    const previousMode = isTranscriptMode ? 'transcript' : 'note';
    const timeInPreviousView = Date.now() - viewModeTimestampRef.current.timestamp;

    // Track switching back to note view
    noteMetrics.viewNoteClicked(noteId, {
      previous_view: previousMode,
      time_in_previous_view_ms: timeInPreviousView,
      encounter_id: encounterId,
    });

    setIsTranscriptMode(false);

    // Update timestamp ref
    viewModeTimestampRef.current = {
      timestamp: Date.now(),
      mode: 'note',
    };
  }, [noteId, encounterId, isTranscriptMode, noteMetrics]);

  return {
    viewMode,
    isTranscriptMode,
    enterTranscriptMode,
    exitTranscriptMode,
  };
}

